import { useCallback, useEffect, useState } from 'react';
import { useUsersList } from './useUsersList';
import { useRecoilValue } from 'recoil';
import { authenticationStateAtom } from '../state/authenticationState';
import { jwtDecode } from 'jwt-decode';
import { decode } from 'base-64';

global.atob = decode;

export type feature =
  | 'requests'
  | 'customers'
  | 'scheduling'
  | 'appointments'
  | 'cancel.appointment'
  | 'messaging'
  | 'documents'
  | 'reporting'
  | 'medication.manage'
  | 'billing.refunds'
  | 'billing.discountcodes'
  | 'referrals.manage'
  | 'treatmentplan'
  | 'notes'
  | 'prescriptions'
  | 'phone'
  | 'pathology'
  | 'delivery'
  | 'tasks'
  | 'allTasks'
  | 'fulfill'
  | 'confirmation'
  | 'subscriptions'
  | 'letters';

const externalDoctorFeatures: feature[] = [
  'documents',
  'appointments',
  'messaging',
  'notes',
  'phone',
  'customers',
  'tasks',
  'pathology',
  'prescriptions',
  'subscriptions',
];

const supportFeatures: feature[] = [
  'fulfill',
  'customers',
  'documents',
  'appointments',
  'cancel.appointment',
  'messaging',
  'notes',
  'phone',
  'requests',
  'tasks',
  'allTasks',
  'prescriptions',
  'confirmation',
  'subscriptions',
  'letters'
];

export const usePortalPermissions = () => {
  const { isLoading, user, accessToken, isAuthenticated } = useRecoilValue(authenticationStateAtom);

  const [permissions, setPermissions] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);

  const [loaded, setLoaded] = useState(false);

  const { findUser } = useUsersList();

  const platformuser = findUser(user?.sub??'');

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const payload = jwtDecode(accessToken);

    const permissions = payload?.['permissions'] ?? [];
    const brands = payload?.['brands'] ?? [];

    setBrands(brands);
    setPermissions(permissions ?? []);
    setLoaded(true);
  }, [accessToken]);

  const hasAnyPermissions = useCallback(() => {
    return permissions.length > 0;
  }, [permissions]);

  const hasPermission = useCallback(
    (feature: feature) => {
      if (permissions.includes(feature)) return true;

      if (permissions.includes('onlinedoctor.internal')) return true;

      if (permissions.includes('onlinedoctor.fulfill')) {
        return feature === 'fulfill';
      }

      if (permissions.includes('onlinedoctor.support')) {
        return supportFeatures.includes(feature);
      }

      if (permissions.includes('onlinedoctor.externaldoctor')) {
        return externalDoctorFeatures.includes(feature);
      }

      return false;
    },
    [permissions],
  );

  const isExternalDoctor = permissions.includes('onlinedoctor.externaldoctor');

  return {
    hasPermission,
    permissions,
    isExternalDoctor,
    hasAnyPermissions,
    isAuthenticated,
    user,
    accessToken,
    isLoading,
    brands,
    loaded,
    platformuser,
    isDoctor: platformuser?.doctor ?? false,
  };
};
